import * as React from "react";
import { cn } from "../../../lib/utils";

const EASE = "ease-[cubic-bezier(0.4,0,0.2,1)]";

type AuthInputProps = React.ComponentPropsWithRef<"input"> & {
  /** Marks the field as failing validation; also sets `aria-invalid`. */
  invalid?: boolean;
  /**
   * Settles the field into a compact, tinted, read-only row. Used once the
   * email has been resolved and the next step has unfolded beneath it.
   */
  locked?: boolean;
};

/** Native text input styled to the auth control tokens. */
export function AuthInput({
  className,
  invalid = false,
  locked = false,
  ...props
}: AuthInputProps) {
  return (
    <input
      aria-invalid={invalid || undefined}
      readOnly={locked || props.readOnly}
      tabIndex={locked ? -1 : props.tabIndex}
      className={cn(
        "h-12 w-full rounded-control border border-hairline-strong bg-white px-4 text-md leading-6 text-ink outline-none",
        "placeholder:text-ink-faint/60",
        `transition-[height,background-color,border-color,box-shadow,color] duration-300 ${EASE}`,
        "hover:border-ink-faint/60",
        "focus-visible:border-brand-600 focus-visible:ring-2 focus-visible:ring-brand-600/20 focus-visible:outline-none",
        "disabled:cursor-not-allowed disabled:bg-tint disabled:text-ink-faint",
        invalid &&
          "border-error-600 focus-visible:border-error-600 focus-visible:ring-error-600/20",
        locked && "h-10 cursor-default border-hairline bg-tint text-ink-muted",
        className
      )}
      {...props}
    />
  );
}

type AuthFieldProps = Omit<AuthInputProps, "id"> & {
  id: string;
  label: React.ReactNode;
  /** Rendered at the right end of the label row (e.g. "Forgot password?" / "Edit"). */
  labelAction?: React.ReactNode;
  /** Positioned inside the input on the right (e.g. a show/hide toggle). */
  rightElement?: React.ReactNode;
  /** Inline validation message. */
  error?: string | null;
  /** Small helper line under the input, shown when there is no error. */
  hint?: React.ReactNode;
};

/** Label row, input, and inline error; the building block of every auth step. */
export const AuthField = React.forwardRef<HTMLInputElement, AuthFieldProps>(
  function AuthField(
    {
      id,
      label,
      labelAction,
      rightElement,
      error,
      hint,
      className,
      invalid,
      ...input
    },
    ref
  ) {
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;
    const describedBy = error ? errorId : hint ? hintId : undefined;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-4">
          <label
            htmlFor={id}
            className="text-md leading-6 font-medium text-ink"
          >
            {label}
          </label>
          {labelAction}
        </div>
        <div className="relative">
          <AuthInput
            ref={ref}
            id={id}
            invalid={Boolean(error) || Boolean(invalid)}
            aria-describedby={describedBy}
            className={cn(rightElement && "pr-12", className)}
            {...input}
          />
          {rightElement}
        </div>
        {error ? (
          <p id={errorId} className="text-base leading-5 text-error-700">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-base leading-5 text-ink-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
);
